//  Purpose: Firebase Auth Store
import axios from 'axios';
import { create } from 'zustand';
import { Device } from '@capacitor/device';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
// import { doc, getDoc, setDoc, collection, getFirestore } from 'firebase/firestore';
import {
  // User,
  // getAuth,
  getAuth,
  signOut,
  initializeAuth,
  onAuthStateChanged,
  signInWithCustomToken,
  sendPasswordResetEmail,
  indexedDBLocalPersistence,
  // signInWithEmailAndPassword,
  // createUserWithEmailAndPassword,
} from 'firebase/auth';

import { trackedApiFetch } from 'src/utils/http/tracked-fetch';
import { recordAuthV3Diagnostic } from 'src/utils/Services/auth-v3/diagnostics';
import { clearApplicationDataForLogout } from 'src/utils/Services/logout-cleanup.service';
import {
  SECURE_STORAGE_KEYS,
  secureStorageService,
} from 'src/utils/Services/secure-storage.service';
import { revokeOperatorSessionBeforePrimaryLogout } from 'src/utils/Services/operator-session-logout-bridge';
import {
  loadDeviceSession,
  invalidateDeviceSessionCache,
} from 'src/utils/Services/auth-v3/session-store';
import {
  getOrCreateDeviceIdentity,
  requestPersistentDeviceIdentityStorage,
} from 'src/utils/Services/auth-v3/device-identity';

import { hasCapability } from 'src/platform';
import { AuthUserType } from 'src/auth/types';
import { useSystemStore } from 'src/store/SystemStore';
import { clearOperatorSession } from 'src/store/OperatorAuthentication';
import { isOfflineDemoActiveAtBoot } from 'src/features/offline-demo/boot';
import {
  AuthOperationError,
  classifyAuthOperationError,
  isTransientAuthOperationFailure,
  isDefinitivelyInvalidCustomToken,
} from 'src/auth/errors/auth-operation-error';

import { getFirebaseWebApp } from './lib';
import { type AuthLogoutReason, shouldPreservePairingForLogout } from './logout-reason';
import {
  hasWebSdkSyncFailure,
  markWebSdkSyncFailure,
  clearWebSdkSyncFailure,
  hasRequiredWebSdkClaims,
  getWebSdkSyncFailureReason,
  needsWebSdkReauthentication,
} from './web-sdk-sync-state';

// Conditional logger - only logs in development mode
const isDev = import.meta.env.DEV;
const authLogger = {
  log: (...args: unknown[]): void => {
    if (isDev) console.log('[AuthStore]', ...args);
  },
  warn: (...args: unknown[]): void => {
    if (isDev) console.warn('[AuthStore]', ...args);
  },
  error: (...args: unknown[]): void => {
    if (isDev) console.error('[AuthStore]', ...args);
  },
};

// Unique ID to detect module duplication issues
export const AUTH_MODULE_ID = Math.random().toString(36).substring(7);
authLogger.log('MODULE LOADED, ID:', AUTH_MODULE_ID);

/**
 * Wrap a promise with a timeout to prevent hanging on iOS.
 * Web SDK operations can hang indefinitely due to CORS/GAPI issues in Capacitor WebView.
 */
function withTimeout<T>(promise: Promise<T>, ms: number, errorMessage: string): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout>;
  const timeoutPromise = new Promise<T>((_, reject) => {
    timeoutId = setTimeout(
      () => reject(new AuthOperationError(errorMessage, { kind: 'TIMEOUT' })),
      ms
    );
  });

  return Promise.race([promise, timeoutPromise]).finally(() => {
    clearTimeout(timeoutId);
  });
}

// Timeout for web SDK sync operations (5 seconds)
const WEB_SDK_SYNC_TIMEOUT = 5000;
const AUTH_RESTORATION_TIMEOUT = 10000;
// Abort ceiling for the backend refreshCustomToken fetch: without a signal the
// request has hung 75s+ on dead tunnels/captive portals. An abort lands in the
// same catch as any other transient failure.
const REFRESH_CUSTOM_TOKEN_FETCH_TIMEOUT_MS = 15000;
const BUSINESS_DEVICE_UID_PREFIX = 'BUSINESS_DEVICE:';

export type AuthRestorationStatus =
  | 'RESTORING'
  | 'AUTHENTICATED'
  | 'SIGNED_OUT'
  | 'RECOVERY_REQUIRED';

// Promise that resolves when Firebase auth state is first determined
let authReadyPromise: Promise<void> | null = null;
let authReadyResolve: (() => void) | null = null;

// Track token refresh interval to prevent multiple intervals
let tokenRefreshIntervalId: NodeJS.Timeout | null = null;

// Track if auth ready has been resolved to prevent double-resolution
let authReadyResolved = false;
let authReadyTimeoutId: ReturnType<typeof setTimeout> | null = null;

function resolveAuthReady(): void {
  if (!authReadyResolve || authReadyResolved) return;
  if (authReadyTimeoutId) clearTimeout(authReadyTimeoutId);
  authReadyTimeoutId = null;
  authReadyResolved = true;
  authReadyResolve();
}

async function getBoundedWebSdkTokenResult(
  user: NonNullable<ReturnType<typeof getFirebaseAuth>['currentUser']>
) {
  try {
    return await withTimeout(
      user.getIdTokenResult(false),
      WEB_SDK_SYNC_TIMEOUT,
      'Web SDK token enrichment timed out'
    );
  } catch {
    return null;
  }
}

function toAuthUser(user: {
  uid: string;
  email?: string | null;
  displayName?: string | null;
  photoUrl?: string | null;
  photoURL?: string | null;
  emailVerified?: boolean;
}): AuthUserType {
  return {
    uid: user.uid,
    email: user.email || null,
    displayName: user.displayName || null,
    photoURL: user.photoUrl || user.photoURL || null,
    emailVerified: user.emailVerified || false,
    id: user.uid,
    role: 'admin',
  };
}

async function verifyOfflineIdentity(uid: string): Promise<boolean> {
  if (!uid.startsWith(BUSINESS_DEVICE_UID_PREFIX)) return true;

  try {
    const session = await loadDeviceSession();
    // Existing Auth V2 devices can already use the deterministic business-device
    // Firebase UID but predate the secure Auth V3 session record. Preserve that
    // legacy offline path; once a V3 record exists, its identity must match.
    if (!session) return true;
    const storedFirebaseUid =
      session.firebaseUid || `${BUSINESS_DEVICE_UID_PREFIX}${session.businessDeviceId}`;
    return storedFirebaseUid === uid;
  } catch (error) {
    authLogger.warn('Unable to verify the stored offline device session:', error);
    return false;
  }
}

async function updateWebSdkClaimsState(
  user: ReturnType<typeof getFirebaseAuth>['currentUser']
): Promise<boolean> {
  if (!user) {
    markWebSdkSyncFailure('TRANSIENT');
    return false;
  }

  try {
    const claimsResult = await getBoundedWebSdkTokenResult(user);
    if (!claimsResult) {
      markWebSdkSyncFailure('TRANSIENT');
      return false;
    }
    const hasRequiredClaims = hasRequiredWebSdkClaims(claimsResult.claims);
    if (hasRequiredClaims) clearWebSdkSyncFailure();
    else markWebSdkSyncFailure('CLAIMS_MISSING');
    return hasRequiredClaims;
  } catch {
    markWebSdkSyncFailure('TRANSIENT');
    return false;
  }
}

// CRITICAL: Track if we've successfully authenticated to prevent race condition resets
// Once this is set to true, auth state should NOT be reset unless explicitly logged out
let authenticationCompleted = false;

/**
 * Check if web SDK sync has failed and re-auth may be needed
 */
export function needsReAuthentication(): boolean {
  return needsWebSdkReauthentication();
}

/**
 * Returns true if the last web SDK auth sync failed.
 * When true, Firestore/RealtimeDB security rules may reject requests
 * because the web SDK has no authenticated user with valid claims.
 */
export function isWebSdkSyncFailed(): boolean {
  return hasWebSdkSyncFailure();
}

/**
 * Attempt to sync web SDK auth using a fresh custom token from the backend.
 * This is called when the stored custom token has expired.
 * Returns true if sync was successful, false otherwise.
 */
export async function attemptWebSdkSyncWithFreshToken(): Promise<boolean> {
  authLogger.log('Attempting to get fresh custom token for web SDK sync...');

  try {
    // Get the native ID token to authenticate with backend
    const idTokenResult = await FirebaseAuthentication.getIdToken({ forceRefresh: false });
    if (!idTokenResult.token) {
      authLogger.error('Failed to get native ID token for refresh');
      return false;
    }

    // Get device info for the request
    const [deviceInfo, deviceId] = await Promise.all([Device.getInfo(), Device.getId()]);

    // Request a fresh custom token from the backend
    const { baseUrl } = useSystemStore.getState();
    const abortController = new AbortController();
    const abortTimer = setTimeout(
      () => abortController.abort(),
      REFRESH_CUSTOM_TOKEN_FETCH_TIMEOUT_MS
    );
    let response: Response;
    try {
      response = await trackedApiFetch(`${baseUrl}/api/auth/refreshCustomToken`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idTokenResult.token}`,
        },
        body: JSON.stringify({
          device: {
            id: deviceId.identifier,
            platform: deviceInfo.platform,
            model: deviceInfo.model,
            osVersion: deviceInfo.osVersion,
            manufacturer: deviceInfo.manufacturer,
            isVirtual: deviceInfo.isVirtual,
            webViewVersion: deviceInfo.webViewVersion,
          },
        }),
        signal: abortController.signal,
      });
    } finally {
      clearTimeout(abortTimer);
    }

    if (!response.ok) {
      authLogger.warn('Backend refresh token endpoint returned:', response.status);
      // This endpoint might not exist yet - that's okay, we'll fall back gracefully
      return false;
    }

    const data = await response.json();
    const freshToken = data?.token || data?.result?.token;

    if (!freshToken) {
      authLogger.warn('No token in refresh response');
      return false;
    }

    // Try to sign in with the fresh token (with timeout to prevent hanging on iOS)
    const auth = getFirebaseAuth();
    if (auth) {
      await withTimeout(
        signInWithCustomToken(auth, freshToken),
        WEB_SDK_SYNC_TIMEOUT,
        'Fresh custom token sign-in timed out'
      );
      // Save the fresh token for future use
      await secureStorageService.set(SECURE_STORAGE_KEYS.CUSTOM_TOKEN, freshToken);
      authLogger.log('Successfully synced web SDK with fresh custom token');
      return await updateWebSdkClaimsState(auth.currentUser);
    }
  } catch (error) {
    authLogger.warn('Failed to sync web SDK with fresh token (may have timed out):', error);
    markWebSdkSyncFailure('TRANSIENT');
  }

  return false;
}

/**
 * Returns a promise that resolves when Firebase auth state has been determined.
 * This ensures guards wait for Firebase to restore the session before making auth decisions.
 * Includes a 10 second timeout fallback to prevent the app from hanging indefinitely
 * if onAuthStateChanged doesn't fire (can happen on some Android devices).
 */
export function waitForAuthReady(): Promise<void> {
  authLogger.log('waitForAuthReady called from MODULE:', AUTH_MODULE_ID);
  authLogger.log('waitForAuthReady state:', {
    authReadyResolved,
    hasPromise: !!authReadyPromise,
  });

  if (!authReadyPromise) {
    authLogger.log('Creating new authReadyPromise with 10s timeout');
    authReadyPromise = new Promise<void>((resolve) => {
      authReadyResolve = resolve;

      // Safety timeout: if auth isn't ready in 10 seconds, resolve anyway
      // This prevents the app from hanging forever if onAuthStateChanged doesn't fire
      authReadyTimeoutId = setTimeout(() => {
        if (!authReadyResolved) {
          authLogger.warn('Auth restoration timed out; recovery is required');
          authLogger.warn('Debug state at timeout:', {
            authenticated: useAuthStore.getState().authenticated,
            authInitialized: useAuthStore.getState().authInitialized,
            loading: useAuthStore.getState().loading,
            hasUser: !!useAuthStore.getState().user,
          });
          useAuthStore.setState((state) => ({
            authRestorationStatus: state.authenticated ? 'AUTHENTICATED' : 'RECOVERY_REQUIRED',
            authInitialized: true,
            loading: false,
          }));
          authReadyResolved = true;
          resolve();
        }
      }, AUTH_RESTORATION_TIMEOUT);
    });
  } else {
    authLogger.log('authReadyPromise already exists, reusing from MODULE:', AUTH_MODULE_ID);
  }
  return authReadyPromise;
}

// Cache the auth instance to avoid re-initialization issues
let cachedAuth: ReturnType<typeof initializeAuth> | ReturnType<typeof getAuth> | null = null;

// const DB = getFirestore(firebaseApp);
/**
 * Get Firebase Auth instance for web SDK operations.
 * This is available on all platforms for:
 * - Non-auth operations like getting user claims
 * - Auth operations on web platform
 *
 * Note: For actual auth operations (login, logout, auth state) on native platforms,
 * use FirebaseAuthentication plugin directly in the relevant methods.
 */
export const getFirebaseAuth = () => {
  // Return cached instance if available
  if (cachedAuth) {
    return cachedAuth;
  }

  const firebaseApp = getFirebaseWebApp();

  if (hasCapability('firebaseAuthInit') === 'initialize-auth-indexeddb') {
    // Avoids the GAPI iframe initialization that causes CORS errors on iOS, and
    // the same reasoning applies to Electron's non-https custom-scheme origin.
    // Only custom-token sign-in is used, so no popup/redirect resolver is needed.
    // See: https://github.com/firebase/firebase-js-sdk/issues/5019
    authLogger.log('Using initializeAuth with indexedDBLocalPersistence');
    cachedAuth = initializeAuth(firebaseApp, {
      persistence: indexedDBLocalPersistence,
    });
  } else {
    // Hosted web: getAuth for full browser support
    cachedAuth = getAuth(firebaseApp);
  }

  return cachedAuth;
};

// const FIREBASE_AUTH = getAuth(firebaseAppOnlyForWeb);
// const AUTH = getFirebaseAuth();
interface AuthState {
  user: AuthUserType | null;
  authToken: string | null;
  authenticated: boolean;
  loading: boolean;
  authInitialized: boolean;
  authRestorationStatus: AuthRestorationStatus;
  offlineIdentityVerified: boolean;

  login: (accountNumber: string, code: string, isAdminOverride?: boolean) => Promise<void>;
  // Auth V2: sign in with a Firebase custom token from the business-device pairing flow.
  loginWithCustomToken: (customToken: string) => Promise<void>;
  // loginWithGoogle: () => Promise<void>;
  // loginWithGithub: () => Promise<void>;
  // loginWithTwitter: () => Promise<void>;
  // register: (email: string, password: string, firstName: string, lastName: string) => Promise<void>;
  logout: (options: AuthLogoutOptions) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  getToken: () => Promise<string | null>;
  initializeAuth: () => Promise<void>;
}

export interface AuthLogoutOptions {
  reason: AuthLogoutReason;
}

const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  authToken: null,
  authenticated: false,
  loading: true,
  authInitialized: false, // New state to track auth initialization
  authRestorationStatus: 'RESTORING',
  offlineIdentityVerified: false,

  initializeAuth: async () => {
    // Simply wait for the auth listener to complete its first callback
    // This ensures Firebase has restored the session before we proceed
    await waitForAuthReady();
  },
  login: async (accountNumber, code, isAdminOverride = false) => {
    try {
      // Use different endpoint for admin override
      const endpoint = isAdminOverride
        ? `${useSystemStore.getState().baseAthUrl}/loginUser?type=adminOverride`
        : `${useSystemStore.getState().baseAthUrl}/loginUser?type=verifyCode`;

      const response = await trackedApiFetch(
        endpoint,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            accountNumber,
            verificationCode: code,
            isAdminOverride,
          }),
        },
        { sensitiveRequest: true }
      );
      authLogger.log('login response', response);

      const responseJson = await response.json();
      authLogger.log('Login response parsed:', {
        hasAuthenticationResult: Boolean(responseJson?.token || responseJson?.result?.token),
      });
      if (responseJson == null) {
        throw new AuthOperationError('Invalid verification code', {
          kind: 'AUTHENTICATION',
          code: 'INVALID_VERIFICATION_CODE',
        });
      }
      const token = responseJson?.token || responseJson?.result?.token;

      // Store the custom token securely for potential session restoration
      // Note: Custom tokens expire after 1 hour, but this helps with quick app restarts
      if (hasCapability('nativeFirebaseAuth')) {
        await secureStorageService.set(SECURE_STORAGE_KEYS.CUSTOM_TOKEN, token);
      }

      if (hasCapability('nativeFirebaseAuth')) {
        // On native platforms, sign in on BOTH native plugin AND web SDK
        // Native plugin handles the native Firebase SDK auth
        // Web SDK auth is needed for Firestore/other web SDK services to work
        authLogger.log('Using native Firebase plugin for sign in');
        const result = await FirebaseAuthentication.signInWithCustomToken({ token });
        const { user } = result;

        if (!user) {
          throw new Error('[AuthStore] User not found after native sign in');
        }

        // Preserve any existing Auth V3 signing identity. Legacy account login
        // must never rotate or delete a paired device key.
        await getOrCreateDeviceIdentity(accountNumber);

        // CRITICAL: Also sign in on web SDK so Firestore works
        // Firestore uses web SDK which needs to see the authenticated user
        const auth = getFirebaseAuth();
        if (auth) {
          authLogger.log('Also signing in on web SDK for Firestore compatibility');
          await signInWithCustomToken(auth, token);
          // Check claims WITHOUT force-refresh. The first ID token issued by
          // signInWithCustomToken embeds claims from the custom token payload.
          // Force-refreshing immediately LOSES those embedded claims because Firebase
          // re-issues via the refresh-token path which only includes setCustomUserClaims
          // claims (which the backend must call separately for persistence).
          const webUser = auth.currentUser;
          if (webUser) {
            const claimsResult = await getBoundedWebSdkTokenResult(webUser);
            console.log('[AuthStore] Web SDK claims status after login sync:', {
              hasRequiredClaims: Boolean(
                claimsResult && hasRequiredWebSdkClaims(claimsResult.claims)
              ),
            });
            if (claimsResult && !hasRequiredWebSdkClaims(claimsResult.claims)) {
              console.warn(
                '[AuthStore] Required business claim missing after login sync. Backend must persist business-scoped custom claims across token refreshes.'
              );
            }
            await updateWebSdkClaimsState(webUser);
          }
        }

        // Get ID token from native layer
        const idTokenResult = await FirebaseAuthentication.getIdToken({ forceRefresh: false });
        const authToken = idTokenResult.token;

        // Set authenticated state immediately
        authLogger.log('LOGIN SUCCESS - Setting auth state:', {
          authenticated: true,
          authInitialized: true,
        });

        // CRITICAL: Mark authentication as completed BEFORE setState
        authenticationCompleted = true;

        // Track in window for debugging
        (window as any).__AUTH_DEBUG__?.push({
          event: 'loginSuccess',
          authenticationCompleted: true,
          time: Date.now(),
        });

        useAuthStore.setState({
          user: {
            uid: user.uid,
            email: user.email || null,
            displayName: user.displayName || null,
            photoURL: user.photoUrl || null,
            emailVerified: user.emailVerified || false,
            id: user.uid,
            role: 'admin',
          },
          authToken,
          authenticated: true,
          loading: false,
          authInitialized: true,
          authRestorationStatus: 'AUTHENTICATED',
          offlineIdentityVerified: await verifyOfflineIdentity(user.uid),
        });

        // Verify state was set correctly
        const stateAfterLogin = useAuthStore.getState();
        authLogger.log('🔑 State AFTER login setState:', {
          authenticated: stateAfterLogin.authenticated,
          authInitialized: stateAfterLogin.authInitialized,
          hasUser: !!stateAfterLogin.user,
        });

        // Save the auth token to local storage
        localStorage.setItem('authToken', authToken);

        // CRITICAL FIX: Resolve auth ready promise after successful login
        // This prevents the 10-second timeout from firing since we know auth succeeded
        if (authReadyResolve && !authReadyResolved) {
          authLogger.log('🔑 Auth ready - resolving after successful native login');
          authReadyResolved = true;
          authReadyResolve();
        }
      } else {
        // Use web SDK for authentication
        const auth = getFirebaseAuth();
        if (!auth) {
          throw new Error('[AuthStore] Firebase auth not initialized');
        }

        const userCredential = await signInWithCustomToken(auth, token);
        const { user } = userCredential;

        if (!user) {
          throw new Error('[AuthStore] User not found in login by account token');
        }

        // Preserve any existing Auth V3 signing identity. Legacy account login
        // must never rotate or delete a paired device key.
        await getOrCreateDeviceIdentity(accountNumber);

        // IMPORTANT: Update authentication state immediately after successful login
        // This ensures the redirect happens without waiting for onAuthStateChanged
        const authToken = await user.getIdToken(false);
        await updateWebSdkClaimsState(user);
        const profile = {
          displayName: user.displayName,
          email: user.email,
          photoURL: user.photoURL,
        };

        // Set authenticated state immediately
        useAuthStore.setState({
          user: {
            ...user,
            ...profile,
            id: user.uid,
            role: 'admin',
          },
          authToken,
          authenticated: true,
          loading: false,
          authInitialized: true,
          authRestorationStatus: 'AUTHENTICATED',
          offlineIdentityVerified: await verifyOfflineIdentity(user.uid),
        });

        // Save the auth token to local storage
        localStorage.setItem('authToken', authToken);

        // CRITICAL FIX: Resolve auth ready promise after successful login
        // This prevents the 10-second timeout from firing since we know auth succeeded
        if (authReadyResolve && !authReadyResolved) {
          authLogger.log('Auth ready - resolving after successful web login');
          authReadyResolved = true;
          authReadyResolve();
        }
      }
    } catch (error) {
      authLogger.error('Login error:', error);
      if (error instanceof AuthOperationError && error.code === 'INVALID_VERIFICATION_CODE') {
        throw error;
      }
      throw new Error('Unable to login, by account verification');
    }
  },
  /**
   * Auth V2: sign in directly with a Firebase custom token returned by the
   * business-device pairing /complete (or /session/refresh) endpoint.
   *
   * Mirrors the custom-token portion of login() — native dual sign-in (native plugin
   * + web SDK) on native, web SDK on web — but is fully additive: it does NOT touch
   * the account-number/verification-code flow. State is set directly (on web too) so
   * the emailVerified gate in the web onAuthStateChanged listener never blocks a
   * device session.
   */
  loginWithCustomToken: async (customToken: string) => {
    try {
      if (hasCapability('nativeFirebaseAuth')) {
        // Persist for session restoration and web SDK sync (as login() does).
        await secureStorageService.set(SECURE_STORAGE_KEYS.CUSTOM_TOKEN, customToken);

        const result = await FirebaseAuthentication.signInWithCustomToken({ token: customToken });
        const { user } = result;
        if (!user) {
          throw new Error('[AuthStore] User not found after device custom-token sign in');
        }

        // Also sign in on the web SDK for Firestore/RealtimeDB compatibility (non-fatal).
        const auth = getFirebaseAuth();
        if (auth) {
          await signInWithCustomToken(auth, customToken)
            .then(() => updateWebSdkClaimsState(auth.currentUser))
            .catch((err) => {
              markWebSdkSyncFailure('TRANSIENT');
              authLogger.warn('Web SDK device sign-in failed (non-fatal):', err);
            });
        }

        const idTokenResult = await FirebaseAuthentication.getIdToken({ forceRefresh: false });
        const authToken = idTokenResult.token;

        authenticationCompleted = true;
        useAuthStore.setState({
          user: {
            uid: user.uid,
            email: user.email || null,
            displayName: user.displayName || null,
            photoURL: user.photoUrl || null,
            emailVerified: user.emailVerified || false,
            id: user.uid,
            role: 'admin',
          },
          authToken,
          authenticated: true,
          loading: false,
          authInitialized: true,
          authRestorationStatus: 'AUTHENTICATED',
          offlineIdentityVerified: await verifyOfflineIdentity(user.uid),
        });
        localStorage.setItem('authToken', authToken);

        if (authReadyResolve && !authReadyResolved) {
          authReadyResolved = true;
          authReadyResolve();
        }
      } else {
        const auth = getFirebaseAuth();
        if (!auth) {
          throw new Error('[AuthStore] Firebase auth not initialized');
        }

        const userCredential = await signInWithCustomToken(auth, customToken);
        const { user } = userCredential;
        if (!user) {
          throw new Error('[AuthStore] User not found after device custom-token sign in');
        }

        const authToken = await user.getIdToken(false);
        await updateWebSdkClaimsState(user);

        authenticationCompleted = true;
        useAuthStore.setState({
          user: {
            ...user,
            id: user.uid,
            role: 'admin',
          },
          authToken,
          authenticated: true,
          loading: false,
          authInitialized: true,
          authRestorationStatus: 'AUTHENTICATED',
          offlineIdentityVerified: await verifyOfflineIdentity(user.uid),
        });
        localStorage.setItem('authToken', authToken);

        if (authReadyResolve && !authReadyResolved) {
          authReadyResolved = true;
          authReadyResolve();
        }
      }
    } catch (error) {
      authLogger.error('loginWithCustomToken error:', error);
      throw error instanceof Error ? error : new Error('Unable to sign in with device token');
    }
  },
  // loginWithGoogle: async () => {
  //   const provider = new GoogleAuthProvider();
  //   await signInWithPopup(AUTH, provider);
  // },

  logout: async (options) => {
    authLogger.log('LOGOUT called - resetting authenticationCompleted flag', {
      reason: options.reason,
    });
    recordAuthV3Diagnostic('primary_logout_started', {
      reason: options.reason,
      pairingPreserved: shouldPreservePairingForLogout(options.reason),
    });
    // Invalidate before the first awaited logout step so an in-flight session
    // repair cannot commit identity after this logout began.
    invalidateDeviceSessionCache();

    // Revoke the short-lived operator session while the primary Firebase
    // credential is still available. Local locking is unconditional and a
    // failed revocation must never block the primary logout.
    try {
      await revokeOperatorSessionBeforePrimaryLogout();
    } catch (error) {
      authLogger.warn('Operator session revocation failed during logout:', error);
    } finally {
      clearOperatorSession({ reason: 'PRIMARY_LOGOUT' });
    }

    // CRITICAL: Reset the authentication completed flag on logout
    authenticationCompleted = false;

    let signOutError: unknown;

    // 1. Sign out from both native and web SDK
    try {
      if (hasCapability('nativeFirebaseAuth')) {
        // Sign out using native Firebase plugin
        await FirebaseAuthentication.signOut();
        authLogger.log('Signed out via native plugin');

        // Also sign out from web SDK (for Firestore compatibility)
        const auth = getFirebaseAuth();
        if (auth) {
          await signOut(auth);
          authLogger.log('Also signed out from web SDK');
        }
      } else {
        // Sign out using web SDK
        const auth = getFirebaseAuth();
        if (auth) {
          await signOut(auth);
          authLogger.log('Signed out via web SDK');
        }
      }
    } catch (error) {
      signOutError = error;
      authLogger.error('Firebase sign out failed; continuing local cleanup:', error);
    }

    // 2. Remove every tenant/user/device cache and reset all in-memory stores.
    try {
      await clearApplicationDataForLogout({
        reason: options.reason,
      });
    } finally {
      // 3. Clear token refresh interval and local auth state even if cleanup fails.
      if (tokenRefreshIntervalId) {
        clearInterval(tokenRefreshIntervalId);
        tokenRefreshIntervalId = null;
      }

      set({
        user: null,
        authToken: null,
        authenticated: false,
        loading: false,
        authInitialized: true,
        authRestorationStatus: 'SIGNED_OUT',
        offlineIdentityVerified: false,
      });
      clearWebSdkSyncFailure();
    }

    if (signOutError) {
      throw signOutError instanceof Error
        ? signOutError
        : new Error('Firebase sign out failed', { cause: signOutError });
    }
  },
  forgotPassword: async (email) => {
    if (hasCapability('nativeFirebaseAuth')) {
      // Use native Firebase plugin
      await FirebaseAuthentication.sendPasswordResetEmail({ email });
    } else {
      const auth = getFirebaseAuth();
      if (auth) {
        await sendPasswordResetEmail(auth, email);
      }
    }
  },
  getToken: async () => {
    if (hasCapability('nativeFirebaseAuth')) {
      // Use native Firebase plugin
      try {
        const result = await FirebaseAuthentication.getIdToken({ forceRefresh: false });
        if (!result.token) {
          authLogger.error('getToken: Native token result is empty');
          throw new AuthOperationError('No auth token found', {
            kind: 'AUTHENTICATION',
            code: 'EMPTY_AUTH_TOKEN',
          });
        }
        return result.token;
      } catch (error) {
        authLogger.error('getToken: Failed to get native token:', error);
        const failure = classifyAuthOperationError(error);
        throw new AuthOperationError('Failed to get auth token', failure, { cause: error });
      }
    }

    // Use web SDK
    const auth = getFirebaseAuth();
    if (!auth) {
      authLogger.error('getToken: Firebase auth not initialized');
      throw new AuthOperationError('Firebase auth not initialized', {
        kind: 'AUTHENTICATION',
        code: 'AUTH_NOT_INITIALIZED',
      });
    }

    const user = auth.currentUser;
    if (!user) {
      authLogger.error('getToken: No current user found');
      throw new AuthOperationError('No user found', {
        kind: 'AUTHENTICATION',
        code: 'NO_CURRENT_USER',
      });
    }

    try {
      const authToken = await user.getIdTokenResult(false);
      if (!authToken?.token) {
        authLogger.error('getToken: Token result is empty');
        throw new AuthOperationError('No auth token found', {
          kind: 'AUTHENTICATION',
          code: 'EMPTY_AUTH_TOKEN',
        });
      }
      return authToken.token;
    } catch (error) {
      authLogger.error('getToken: Failed to get token:', error);
      const failure = classifyAuthOperationError(error);
      throw new AuthOperationError('Failed to get auth token', failure, { cause: error });
    }
  },
}));

function publishAuthenticatedUser(
  user: Parameters<typeof toAuthUser>[0],
  authToken: string | null = null
): void {
  const previousState = useAuthStore.getState();
  const canRetainToken = previousState.user?.uid === user.uid;

  authenticationCompleted = true;
  useAuthStore.setState({
    user: toAuthUser(user),
    authToken: authToken || (canRetainToken ? previousState.authToken : null),
    authenticated: true,
    loading: false,
    authInitialized: true,
    authRestorationStatus: 'AUTHENTICATED',
    offlineIdentityVerified: !user.uid.startsWith(BUSINESS_DEVICE_UID_PREFIX),
  });
  resolveAuthReady();

  verifyOfflineIdentity(user.uid)
    .then((verified) => {
      const currentState = useAuthStore.getState();
      if (
        currentState.authRestorationStatus === 'AUTHENTICATED' &&
        currentState.user?.uid === user.uid
      ) {
        useAuthStore.setState({ offlineIdentityVerified: verified });
      }
    })
    .catch(() => undefined);
}

/**
 * On-demand re-check of the offline device identity. The post-login
 * verification above is async and fails closed when loadDeviceSession throws
 * transiently, which can leave a valid business-device login marked
 * unverified. Manual offline entry retries with a fresh session read before
 * refusing; a genuine UID mismatch stays closed.
 */
export async function retryOfflineIdentityVerification(): Promise<boolean> {
  const { user, authenticated, authRestorationStatus, offlineIdentityVerified } =
    useAuthStore.getState();
  if (!authenticated || authRestorationStatus !== 'AUTHENTICATED' || !user) return false;
  if (offlineIdentityVerified) return true;

  const verified = await verifyOfflineIdentity(user.uid);
  const current = useAuthStore.getState();
  if (current.authRestorationStatus === 'AUTHENTICATED' && current.user?.uid === user.uid) {
    useAuthStore.setState({ offlineIdentityVerified: verified });
  }
  return verified;
}

function publishSignedOut(): void {
  authenticationCompleted = false;
  useAuthStore.setState({
    user: null,
    authToken: null,
    authenticated: false,
    loading: false,
    authInitialized: true,
    authRestorationStatus: 'SIGNED_OUT',
    offlineIdentityVerified: false,
  });
  resolveAuthReady();
}

function publishRecoveryRequired(): void {
  const state = useAuthStore.getState();
  if (state.authenticated && state.user) {
    useAuthStore.setState({
      loading: false,
      authInitialized: true,
      authRestorationStatus: 'AUTHENTICATED',
    });
  } else {
    useAuthStore.setState({
      user: null,
      authToken: null,
      authenticated: false,
      loading: false,
      authInitialized: true,
      authRestorationStatus: 'RECOVERY_REQUIRED',
      offlineIdentityVerified: false,
    });
  }
  resolveAuthReady();
}

let authRecoveryPromise: Promise<AuthRestorationStatus> | null = null;

export function recoverAuthSession(): Promise<AuthRestorationStatus> {
  if (authRecoveryPromise) return authRecoveryPromise;

  authRecoveryPromise = (async () => {
    if (hasCapability('nativeFirebaseAuth')) {
      let currentUserResult: Awaited<ReturnType<typeof FirebaseAuthentication.getCurrentUser>>;
      try {
        currentUserResult = await withTimeout(
          FirebaseAuthentication.getCurrentUser(),
          AUTH_RESTORATION_TIMEOUT,
          'Native auth restoration timed out'
        );
      } catch (error) {
        publishRecoveryRequired();
        throw new AuthOperationError(
          'Unable to restore the native auth session',
          classifyAuthOperationError(error),
          { cause: error }
        );
      }

      if (!currentUserResult.user) {
        publishSignedOut();
        return 'SIGNED_OUT';
      }

      publishAuthenticatedUser(currentUserResult.user);

      try {
        const idTokenResult = await withTimeout(
          FirebaseAuthentication.getIdToken({ forceRefresh: false }),
          WEB_SDK_SYNC_TIMEOUT,
          'Native auth token synchronization timed out'
        );
        if (!idTokenResult.token) {
          throw new AuthOperationError('Native auth token is empty', {
            kind: 'AUTHENTICATION',
            code: 'EMPTY_AUTH_TOKEN',
          });
        }
        const state = useAuthStore.getState();
        if (state.user?.uid === currentUserResult.user.uid) {
          useAuthStore.setState({ authToken: idTokenResult.token });
          localStorage.setItem('authToken', idTokenResult.token);
        }
      } catch (error) {
        const failure = classifyAuthOperationError(error);
        if (failure.kind === 'AUTHENTICATION') {
          publishSignedOut();
          return 'SIGNED_OUT';
        }
        if (isTransientAuthOperationFailure(failure) || failure.kind === 'UNKNOWN') {
          // The native user remains the local session authority. Reconnection can
          // retry token enrichment without turning a valid offline user into logout.
          throw new AuthOperationError('Unable to synchronize the restored auth token', failure, {
            cause: error,
          });
        }
        throw error;
      }

      if (hasWebSdkSyncFailure()) {
        attemptWebSdkSyncWithFreshToken().catch(() => undefined);
      }
      return 'AUTHENTICATED';
    }

    const auth = getFirebaseAuth();
    const currentUser = auth?.currentUser;
    if (!currentUser) {
      publishSignedOut();
      return 'SIGNED_OUT';
    }

    try {
      const tokenResult = await withTimeout(
        currentUser.getIdTokenResult(false),
        WEB_SDK_SYNC_TIMEOUT,
        'Web auth restoration timed out'
      );
      publishAuthenticatedUser(currentUser, tokenResult.token);
      return 'AUTHENTICATED';
    } catch (error) {
      publishRecoveryRequired();
      throw new AuthOperationError(
        'Unable to restore the web auth session',
        classifyAuthOperationError(error),
        { cause: error }
      );
    }
  })().finally(() => {
    authRecoveryPromise = null;
  });

  return authRecoveryPromise;
}

// Function to refresh the auth token
const refreshAuthToken = async () => {
  try {
    if (hasCapability('nativeFirebaseAuth')) {
      // Use native Firebase plugin
      const result = await FirebaseAuthentication.getIdToken({ forceRefresh: true });
      if (result.token) {
        useAuthStore.setState({ authToken: result.token });
        localStorage.setItem('authToken', result.token);
        authLogger.log('Token refreshed successfully (native)');
      }
    } else {
      // Use web SDK
      const auth = getFirebaseAuth();
      if (!auth) {
        authLogger.log('No auth instance in refreshAuthToken');
        return;
      }
      const user = auth.currentUser;
      if (!user) {
        authLogger.log('No user found in refreshAuthToken');
        return;
      }
      const token = await user.getIdToken(true);
      useAuthStore.setState({ authToken: token });
      // Persist to localStorage so token survives crashes/refreshes
      localStorage.setItem('authToken', token);
      authLogger.log('Token refreshed successfully (web)');
    }
  } catch (error) {
    authLogger.error('Error refreshing token:', error);
  }
};

// Track whether auth listener has been initialized
let authListenerInitialized = false;

/**
 * Initialize the auth state listener
 * This should be called after Firebase is fully initialized on native platforms
 * to prevent race conditions and Firebase configuration errors
 */
export function initializeAuthListener() {
  if (isOfflineDemoActiveAtBoot()) {
    useAuthStore.setState({
      authInitialized: true,
      loading: false,
      authenticated: false,
      authRestorationStatus: 'SIGNED_OUT',
      offlineIdentityVerified: false,
    });
    return;
  }
  authLogger.log('initializeAuthListener() CALLED');

  // Initialize secure storage and migrate legacy tokens
  secureStorageService
    .initialize()
    .then(() => {
      secureStorageService.migrateFromLocalStorage(SECURE_STORAGE_KEYS.CUSTOM_TOKEN);
    })
    .catch((error) => {
      authLogger.warn('Secure storage initialization failed:', error);
    });

  requestPersistentDeviceIdentityStorage().catch(() => undefined);

  // Also store in window for debugging
  (window as any).__AUTH_DEBUG__ = (window as any).__AUTH_DEBUG__ || [];
  (window as any).__AUTH_DEBUG__.push({ event: 'initializeAuthListener', time: Date.now() });

  if (authListenerInitialized) {
    authLogger.log('Auth listener already initialized - skipping');
    return;
  }

  authLogger.log('First time initialization - proceeding...');
  authListenerInitialized = true;

  // Create the ready promise if it doesn't exist
  if (!authReadyPromise) {
    authLogger.log('Creating authReadyPromise in initializeAuthListener, MODULE:', AUTH_MODULE_ID);
    authReadyPromise = new Promise<void>((resolve) => {
      authReadyResolve = resolve;

      // Safety timeout: if auth isn't ready in 10 seconds, resolve anyway
      authReadyTimeoutId = setTimeout(() => {
        if (!authReadyResolved) {
          authLogger.warn('Auth restoration timed out; recovery is required');
          authLogger.warn('Debug state at timeout:', {
            authenticated: useAuthStore.getState().authenticated,
            authInitialized: useAuthStore.getState().authInitialized,
            loading: useAuthStore.getState().loading,
            hasUser: !!useAuthStore.getState().user,
            moduleId: AUTH_MODULE_ID,
          });
          useAuthStore.setState((state) => ({
            authRestorationStatus: state.authenticated ? 'AUTHENTICATED' : 'RECOVERY_REQUIRED',
            authInitialized: true,
            loading: false,
          }));
          authReadyResolved = true;
          resolve();
        }
      }, AUTH_RESTORATION_TIMEOUT);
    });
  } else {
    authLogger.log(
      'authReadyPromise already exists in initializeAuthListener, MODULE:',
      AUTH_MODULE_ID
    );
  }

  let isFirstCallback = true;
  let initialCurrentUserCheckCompleted = false;
  let pendingInitialNullChange = false;

  if (hasCapability('nativeFirebaseAuth')) {
    // Use native Firebase plugin listener
    authLogger.log('Setting up native authStateChange listener...');

    FirebaseAuthentication.addListener('authStateChange', async (change) => {
      authLogger.log('authStateChange fired:', { hasUser: Boolean(change.user) });
      authLogger.log('State before:', {
        authenticated: useAuthStore.getState().authenticated,
        authInitialized: useAuthStore.getState().authInitialized,
      });

      // Track in window for debugging
      (window as any).__AUTH_DEBUG__?.push({
        event: 'authStateChange',
        hasUser: Boolean(change.user),
        time: Date.now(),
      });

      if (!initialCurrentUserCheckCompleted && !authReadyResolved) {
        pendingInitialNullChange = !change.user;
        authLogger.log('Buffering initial auth event until getCurrentUser resolves');
        return;
      }
      if (!initialCurrentUserCheckCompleted && !change.user && !authenticationCompleted) {
        authLogger.log('Ignoring an unconfirmed null auth event while recovery is pending');
        return;
      }

      try {
        if (change.user) {
          // A native user is sufficient to restore the local session. Token and
          // Web SDK synchronization below are enrichment and must not delay boot.
          publishAuthenticatedUser(change.user);
          isFirstCallback = false;

          // For custom token auth, email verification is handled differently
          // Custom tokens typically don't require email verification check
          const idTokenResult = await withTimeout(
            FirebaseAuthentication.getIdToken({ forceRefresh: false }),
            WEB_SDK_SYNC_TIMEOUT,
            'Native auth token enrichment timed out'
          );

          // CRITICAL: Sync web SDK auth for Firestore compatibility
          // Check if web SDK has the user, if not try to sign in
          let webSdkSynced = false;
          const auth = getFirebaseAuth();

          if (auth?.currentUser) {
            // Web SDK already has user (session restored from IndexedDB).
            // Check if their token still has businessId. After ~1 hour Firefox auto-refreshes
            // the token via the refresh-token path, which drops embedded custom-token claims
            // unless the backend also calls setCustomUserClaims.
            const existingClaims = await getBoundedWebSdkTokenResult(auth.currentUser);
            const hasRequiredClaims = existingClaims
              ? hasRequiredWebSdkClaims(existingClaims.claims)
              : false;
            console.log('[AuthStore] authStateChange — web SDK session restored:', {
              hasRequiredClaims,
            });
            if (hasRequiredClaims) {
              webSdkSynced = true;
              clearWebSdkSyncFailure();
            } else {
              // Claims were lost to token refresh. Try to re-sync with a fresh custom token
              // so the next ID token has the embedded businessId claim again.
              console.warn(
                '[AuthStore] businessId claim missing from restored web SDK session — attempting re-sync...'
              );
              try {
                const freshSyncSuccess = await withTimeout(
                  attemptWebSdkSyncWithFreshToken(),
                  WEB_SDK_SYNC_TIMEOUT,
                  'Fresh token re-sync timed out'
                );
                if (freshSyncSuccess) {
                  const resynced = auth.currentUser;
                  const resyncedClaims = resynced
                    ? await getBoundedWebSdkTokenResult(resynced)
                    : null;
                  console.log('[AuthStore] Web SDK claim re-sync status:', {
                    hasRequiredClaims: Boolean(
                      resyncedClaims && hasRequiredWebSdkClaims(resyncedClaims.claims)
                    ),
                  });
                  webSdkSynced = Boolean(
                    resyncedClaims && hasRequiredWebSdkClaims(resyncedClaims.claims)
                  );
                  if (webSdkSynced) clearWebSdkSyncFailure();
                  else {
                    markWebSdkSyncFailure(resyncedClaims ? 'CLAIMS_MISSING' : 'TRANSIENT');
                  }
                } else {
                  markWebSdkSyncFailure('TRANSIENT');
                  console.warn(
                    '[AuthStore] Re-sync failed — Firestore will reject until backend calls setCustomUserClaims or user re-logs in.'
                  );
                }
              } catch (resyncErr) {
                markWebSdkSyncFailure('TRANSIENT');
                authLogger.warn('Re-sync attempt threw:', resyncErr);
              }
            }
          } else if (auth) {
            authLogger.log('Web SDK has no user, attempting to sync...');
            const storedToken = await secureStorageService.get(SECURE_STORAGE_KEYS.CUSTOM_TOKEN);
            if (storedToken) {
              try {
                // Add timeout to prevent hanging on iOS due to CORS/GAPI issues
                await withTimeout(
                  signInWithCustomToken(auth, storedToken),
                  WEB_SDK_SYNC_TIMEOUT,
                  'Web SDK sync timed out'
                );
                authLogger.log('Successfully synced web SDK auth');
                // Check claims WITHOUT force-refresh — the first token from signInWithCustomToken
                // has the embedded businessId. Force-refreshing would strip it.
                const webUserAfterSync = auth.currentUser;
                if (webUserAfterSync) {
                  const claimsResult = await getBoundedWebSdkTokenResult(webUserAfterSync);
                  console.log('[AuthStore] Web SDK claims status after auth state sync:', {
                    hasRequiredClaims: Boolean(
                      claimsResult && hasRequiredWebSdkClaims(claimsResult.claims)
                    ),
                  });
                }
                webSdkSynced = await updateWebSdkClaimsState(webUserAfterSync);
              } catch (syncError) {
                // Custom token may have expired (1 hour validity) or timed out
                authLogger.warn(
                  'Failed to sync web SDK auth (token may have expired or timed out):',
                  syncError
                );
                if (isDefinitivelyInvalidCustomToken(syncError)) {
                  await secureStorageService.remove(SECURE_STORAGE_KEYS.CUSTOM_TOKEN);
                }

                // Try to get a fresh custom token from backend with timeout
                try {
                  const freshSyncSuccess = await withTimeout(
                    attemptWebSdkSyncWithFreshToken(),
                    WEB_SDK_SYNC_TIMEOUT,
                    'Fresh token sync timed out'
                  );
                  if (freshSyncSuccess) {
                    webSdkSynced = true;
                    clearWebSdkSyncFailure();
                  } else {
                    markWebSdkSyncFailure('TRANSIENT');
                  }
                } catch (freshError) {
                  authLogger.warn('Fresh token sync also failed:', freshError);
                  markWebSdkSyncFailure('TRANSIENT');
                }
              }
            } else {
              authLogger.warn('No stored custom token for web SDK sync');
              // Try to get a fresh custom token from backend with timeout
              try {
                const freshSyncSuccess = await withTimeout(
                  attemptWebSdkSyncWithFreshToken(),
                  WEB_SDK_SYNC_TIMEOUT,
                  'Fresh token sync timed out'
                );
                if (freshSyncSuccess) {
                  webSdkSynced = true;
                  clearWebSdkSyncFailure();
                } else {
                  markWebSdkSyncFailure('TRANSIENT');
                }
              } catch (freshError) {
                authLogger.warn('Fresh token sync failed:', freshError);
                markWebSdkSyncFailure('TRANSIENT');
              }
            }
          }

          // Set authenticated state - trust native auth even if web SDK sync failed
          // API calls will work (use native token), but Firestore operations may fail
          authLogger.log('authStateChange - publishing authenticated state');
          useAuthStore.setState({
            user: {
              uid: change.user.uid,
              email: change.user.email || null,
              displayName: change.user.displayName || null,
              photoURL: change.user.photoUrl || null,
              emailVerified: change.user.emailVerified || false,
              id: change.user.uid,
              role: 'admin',
            },
            authToken: idTokenResult.token,
            authenticated: true, // Trust native auth
            loading: false,
            authInitialized: true,
            authRestorationStatus: 'AUTHENTICATED',
          });

          // CRITICAL: Mark authentication as completed
          authenticationCompleted = true;

          authLogger.log('authStateChange - State AFTER set:', {
            authenticated: useAuthStore.getState().authenticated,
            authInitialized: useAuthStore.getState().authInitialized,
            authenticationCompleted: true,
          });

          // Save the auth token to local storage
          localStorage.setItem('authToken', idTokenResult.token);

          // Log web SDK sync status for debugging
          if (!webSdkSynced) {
            authLogger.warn(
              'Web SDK not synced in authStateChange - Firestore operations may fail'
            );
            authLogger.warn('webSdkSyncFailureReason =', getWebSdkSyncFailureReason());
          } else {
            authLogger.log('Web SDK synced successfully in authStateChange');
          }

          // Set up token refresh - only once!
          if (!tokenRefreshIntervalId) {
            tokenRefreshIntervalId = setInterval(() => refreshAuthToken(), 50 * 60 * 1000);
          }
        } else {
          // No user - THIS IS CRITICAL - if this fires after login, it would cause redirect loop
          const currentState = useAuthStore.getState();
          authLogger.log('authStateChange - NO USER RECEIVED');
          authLogger.log('authenticationCompleted:', authenticationCompleted);
          authLogger.log('State BEFORE potential reset:', {
            authenticated: currentState.authenticated,
            authInitialized: currentState.authInitialized,
            hasUser: !!currentState.user,
          });

          // Track in window for debugging
          (window as any).__AUTH_DEBUG__?.push({
            event: 'authStateChange_NO_USER',
            authenticationCompleted,
            stateBefore: {
              authenticated: currentState.authenticated,
              hasUser: !!currentState.user,
            },
            time: Date.now(),
          });

          // CRITICAL: Check multiple conditions before resetting
          // 1. authenticationCompleted flag (set after successful auth)
          // 2. currentState.authenticated (zustand state)
          // 3. currentState.user (actual user object)
          if (authenticationCompleted) {
            authLogger.log('SKIPPING RESET - authenticationCompleted=true');
            authLogger.log('This is likely a race condition - ignoring null user');
          } else if (currentState.authenticated && currentState.user) {
            authLogger.log('SKIPPING RESET - already authenticated with user in state');
            authLogger.log('This might be a race condition - ignoring null user');
          } else {
            authLogger.log('Resetting auth state to unauthenticated');
            useAuthStore.setState({
              user: null,
              authToken: null,
              authenticated: false,
              loading: false,
              authInitialized: true,
              authRestorationStatus: 'SIGNED_OUT',
              offlineIdentityVerified: false,
            });
          }
        }
      } catch (error) {
        const failure = classifyAuthOperationError(error);
        authLogger.error('Native auth state enrichment failed:', { kind: failure.kind });

        // Track the error
        (window as any).__AUTH_DEBUG__?.push({
          event: 'authStateChange_ERROR',
          failureKind: failure.kind,
          authenticationCompleted,
          time: Date.now(),
        });

        // CRITICAL: Only reset if authentication wasn't already completed
        if (authenticationCompleted) {
          authLogger.warn('Error occurred but authenticationCompleted=true, NOT resetting');
        } else {
          authLogger.error('Native auth state could not be restored');
          publishRecoveryRequired();
        }
      } finally {
        // Resolve the ready promise on first callback
        if (isFirstCallback && !(pendingInitialNullChange && !initialCurrentUserCheckCompleted)) {
          isFirstCallback = false;
          authLogger.log('Auth ready (native) - resolving promise');
          resolveAuthReady();
        }
      }
    });

    // Check current user immediately to handle auth state
    // This is critical because authStateChange may not fire if state hasn't changed
    authLogger.log('Calling getCurrentUser()...');
    FirebaseAuthentication.getCurrentUser()
      .then(async (result) => {
        initialCurrentUserCheckCompleted = true;
        authLogger.log(
          'getCurrentUser RESULT:',
          result.user ? 'USER' : 'NO USER',
          'isFirstCallback:',
          isFirstCallback
        );

        // Track in window for debugging
        (window as any).__AUTH_DEBUG__?.push({
          event: 'getCurrentUser',
          hasUser: Boolean(result.user),
          isFirstCallback,
          time: Date.now(),
        });

        const shouldInitializeFromCurrentUser = Boolean(result.user && isFirstCallback);

        if (result.user && shouldInitializeFromCurrentUser) {
          // CRITICAL FIX: User exists - initialize auth state immediately
          // Don't wait for authStateChange which may not fire if state hasn't changed
          authLogger.log('getCurrentUser - USER FOUND, initializing auth...');

          publishAuthenticatedUser(result.user);
          isFirstCallback = false;

          try {
            // Get ID token
            authLogger.log('getCurrentUser - Getting ID token...');
            const idTokenResult = await withTimeout(
              FirebaseAuthentication.getIdToken({ forceRefresh: false }),
              WEB_SDK_SYNC_TIMEOUT,
              'Native auth token enrichment timed out'
            );
            authLogger.log('getCurrentUser - Got ID token:', idTokenResult.token ? 'YES' : 'NO');

            // Attempt web SDK sync for Firestore compatibility
            let webSdkSynced = false;
            const auth = getFirebaseAuth();

            if (auth?.currentUser) {
              // Web SDK session restored from IndexedDB. Check if businessId claim is still present.
              const existingClaims = await getBoundedWebSdkTokenResult(auth.currentUser);
              const hasRequiredClaims = existingClaims
                ? hasRequiredWebSdkClaims(existingClaims.claims)
                : false;
              console.log('[AuthStore] getCurrentUser — web SDK session restored:', {
                hasRequiredClaims,
              });
              if (hasRequiredClaims) {
                webSdkSynced = true;
                clearWebSdkSyncFailure();
              } else {
                console.warn(
                  '[AuthStore] businessId claim missing from restored web SDK session (getCurrentUser) — attempting re-sync...'
                );
                try {
                  const freshSyncSuccess = await withTimeout(
                    attemptWebSdkSyncWithFreshToken(),
                    WEB_SDK_SYNC_TIMEOUT,
                    'Fresh token re-sync timed out'
                  );
                  if (freshSyncSuccess) {
                    const resynced = auth.currentUser;
                    const resyncedClaims = resynced
                      ? await getBoundedWebSdkTokenResult(resynced)
                      : null;
                    console.log('[AuthStore] getCurrentUser claim re-sync status:', {
                      hasRequiredClaims: Boolean(
                        resyncedClaims && hasRequiredWebSdkClaims(resyncedClaims.claims)
                      ),
                    });
                    webSdkSynced = Boolean(
                      resyncedClaims && hasRequiredWebSdkClaims(resyncedClaims.claims)
                    );
                    if (webSdkSynced) clearWebSdkSyncFailure();
                    else {
                      markWebSdkSyncFailure(resyncedClaims ? 'CLAIMS_MISSING' : 'TRANSIENT');
                    }
                  } else {
                    markWebSdkSyncFailure('TRANSIENT');
                    console.warn(
                      '[AuthStore] getCurrentUser re-sync failed — Firestore will reject until backend fixes claims.'
                    );
                  }
                } catch (resyncErr) {
                  markWebSdkSyncFailure('TRANSIENT');
                  authLogger.warn('getCurrentUser re-sync threw:', resyncErr);
                }
              }
            } else if (auth) {
              authLogger.log('Attempting web SDK sync from getCurrentUser...');
              const storedToken = await secureStorageService.get(SECURE_STORAGE_KEYS.CUSTOM_TOKEN);
              if (storedToken) {
                try {
                  // Add timeout to prevent hanging on iOS due to CORS/GAPI issues
                  await withTimeout(
                    signInWithCustomToken(auth, storedToken),
                    WEB_SDK_SYNC_TIMEOUT,
                    'Web SDK sync timed out'
                  );
                  authLogger.log('Successfully synced web SDK auth (getCurrentUser path)');
                  // Check claims WITHOUT force-refresh (preserve embedded custom-token claims)
                  const webUserAfterSync = auth.currentUser;
                  if (webUserAfterSync) {
                    const claimsResult = await getBoundedWebSdkTokenResult(webUserAfterSync);
                    console.log('[AuthStore] Web SDK claims status after current-user sync:', {
                      hasRequiredClaims: Boolean(
                        claimsResult && hasRequiredWebSdkClaims(claimsResult.claims)
                      ),
                    });
                  }
                  webSdkSynced = await updateWebSdkClaimsState(webUserAfterSync);
                } catch (syncError) {
                  authLogger.warn(
                    'Web SDK sync failed in getCurrentUser (token may have expired or timed out):',
                    syncError
                  );
                  if (isDefinitivelyInvalidCustomToken(syncError)) {
                    await secureStorageService.remove(SECURE_STORAGE_KEYS.CUSTOM_TOKEN);
                  }

                  // Try to get a fresh custom token from backend with timeout
                  try {
                    const freshSyncSuccess = await withTimeout(
                      attemptWebSdkSyncWithFreshToken(),
                      WEB_SDK_SYNC_TIMEOUT,
                      'Fresh token sync timed out'
                    );
                    if (freshSyncSuccess) {
                      webSdkSynced = true;
                      clearWebSdkSyncFailure();
                    } else {
                      markWebSdkSyncFailure('TRANSIENT');
                    }
                  } catch (freshError) {
                    authLogger.warn('Fresh token sync also failed:', freshError);
                    markWebSdkSyncFailure('TRANSIENT');
                  }
                }
              } else {
                // No stored token - try to get a fresh one with timeout
                try {
                  const freshSyncSuccess = await withTimeout(
                    attemptWebSdkSyncWithFreshToken(),
                    WEB_SDK_SYNC_TIMEOUT,
                    'Fresh token sync timed out'
                  );
                  if (freshSyncSuccess) {
                    webSdkSynced = true;
                    clearWebSdkSyncFailure();
                  } else {
                    markWebSdkSyncFailure('TRANSIENT');
                  }
                } catch (freshError) {
                  authLogger.warn('Fresh token sync failed:', freshError);
                  markWebSdkSyncFailure('TRANSIENT');
                }
              }
            }

            // Set auth state - trust native auth even if web SDK sync failed
            authLogger.log('getCurrentUser - Setting auth state NOW...');
            useAuthStore.setState({
              user: {
                uid: result.user.uid,
                email: result.user.email || null,
                displayName: result.user.displayName || null,
                photoURL: result.user.photoUrl || null,
                emailVerified: result.user.emailVerified || false,
                id: result.user.uid,
                role: 'admin',
              },
              authToken: idTokenResult.token,
              authenticated: true, // Trust native auth
              loading: false,
              authInitialized: true,
              authRestorationStatus: 'AUTHENTICATED',
            });

            // CRITICAL: Mark authentication as completed to prevent race condition resets
            authenticationCompleted = true;

            // VERIFY the state was actually set
            const verifyState = useAuthStore.getState();
            authLogger.log('getCurrentUser - State AFTER setState:', {
              authenticated: verifyState.authenticated,
              authInitialized: verifyState.authInitialized,
              hasUser: !!verifyState.user,
              userId: verifyState.user?.uid,
              authenticationCompleted: true,
            });

            // Track in debug array
            (window as any).__AUTH_DEBUG__?.push({
              event: 'getCurrentUser_STATE_SET',
              authenticated: verifyState.authenticated,
              authInitialized: verifyState.authInitialized,
              authenticationCompleted: true,
              time: Date.now(),
            });

            localStorage.setItem('authToken', idTokenResult.token);

            if (!webSdkSynced) {
              authLogger.log('getCurrentUser - Web SDK NOT synced, Firestore may fail');
            } else {
              authLogger.log('getCurrentUser - Web SDK synced OK');
            }

            // Set up token refresh if not already set
            if (!tokenRefreshIntervalId) {
              tokenRefreshIntervalId = setInterval(() => refreshAuthToken(), 50 * 60 * 1000);
            }
          } catch (error) {
            const failure = classifyAuthOperationError(error);
            authLogger.log('getCurrentUser enrichment failed:', { kind: failure.kind });
            authLogger.error('authenticationCompleted:', authenticationCompleted);

            // Track the error
            (window as any).__AUTH_DEBUG__?.push({
              event: 'getCurrentUser_ERROR',
              failureKind: failure.kind,
              authenticationCompleted,
              time: Date.now(),
            });

            // CRITICAL: Only reset if authentication wasn't already completed elsewhere
            if (authenticationCompleted) {
              authLogger.warn('getCurrentUser - Error but auth already completed, NOT resetting');
            } else {
              authLogger.log('getCurrentUser enrichment failed before local restoration');
              publishRecoveryRequired();
            }
          }

          // Resolve auth ready
          if (!authReadyResolved) {
            authLogger.log('getCurrentUser - RESOLVING AUTH READY (with user)');
            resolveAuthReady();
            authLogger.log('getCurrentUser - AUTH READY RESOLVED!');

            // Track this critical moment
            (window as any).__AUTH_DEBUG__?.push({
              event: 'AUTH_READY_RESOLVED_WITH_USER',
              time: Date.now(),
            });
          }
        } else if (!result.user && isFirstCallback) {
          // No user - resolve auth ready
          authLogger.log('getCurrentUser - NO USER found, showing login');
          isFirstCallback = false;
          pendingInitialNullChange = false;
          publishSignedOut();
          if (!authReadyResolved) {
            authLogger.log('getCurrentUser - RESOLVING AUTH READY (no user)');
            resolveAuthReady();
            authLogger.log('getCurrentUser - AUTH READY RESOLVED (no user)!');

            // Track this
            (window as any).__AUTH_DEBUG__?.push({
              event: 'AUTH_READY_RESOLVED_NO_USER',
              time: Date.now(),
            });
          }
        }
      })
      .catch((error) => {
        const failure = classifyAuthOperationError(error);
        authLogger.error('getCurrentUser failed:', { kind: failure.kind });
        if (isFirstCallback) {
          isFirstCallback = false;
          pendingInitialNullChange = false;
          authLogger.log('Auth recovery is required after getCurrentUser error');
          publishRecoveryRequired();
        }
      });
  } else {
    // Use web SDK listener
    authLogger.log('Setting up onAuthStateChanged listener...');
    const auth = getFirebaseAuth();
    if (!auth) {
      authLogger.warn('Firebase auth not initialized for web');
      publishRecoveryRequired();
      return;
    }
    authLogger.log('Got Firebase Auth instance:', { hasUser: Boolean(auth.currentUser) });

    onAuthStateChanged(auth, async (user) => {
      authLogger.log('onAuthStateChanged callback fired:', { hasUser: Boolean(user) });
      try {
        if (user) {
          // Custom-token sessions — Auth V2 device pairing (uid = `businessDevice:{id}`)
          // and Auth V3 passwordless Manager login — are minted server-side and trusted,
          // but have no verified email. Treat them as authenticated alongside regular
          // email-verified users. `signInProvider === 'custom'` covers both and subsumes
          // the old businessDevice: uid prefix check. Regular logins are unaffected
          // (signInProvider === 'password' still requires emailVerified).
          //
          // Use getIdTokenResult() WITHOUT forceRefresh: force-refreshing a restored
          // custom-token session strips the embedded businessId claim (see native path),
          // which Firestore relies on.
          const tokenResult = await user.getIdTokenResult();
          const isCustomTokenUser = tokenResult.signInProvider === 'custom';
          if (user.emailVerified || isCustomTokenUser) {
            const profile = {
              displayName: user.displayName,
              email: user.email,
              photoURL: user.photoURL,
            };

            const authToken = tokenResult.token;

            useAuthStore.setState({
              user: {
                ...user,
                ...profile,
                id: user.uid,
                role: 'admin',
              },
              authToken,
              authenticated: true,
              loading: false,
              authInitialized: true,
              authRestorationStatus: 'AUTHENTICATED',
              offlineIdentityVerified: await verifyOfflineIdentity(user.uid),
            });

            // Save the auth token to local storage
            localStorage.setItem('authToken', authToken);

            // Set up token refresh - only once!
            if (!tokenRefreshIntervalId) {
              tokenRefreshIntervalId = setInterval(() => refreshAuthToken(), 50 * 60 * 1000);
            }
          } else {
            // User exists but email not verified and not a custom-token session
            publishSignedOut();
          }
        } else {
          // No user
          publishSignedOut();
        }
      } catch (error) {
        authLogger.error('Error in onAuthStateChanged:', error);
        publishRecoveryRequired();
      } finally {
        // Resolve the ready promise on first callback
        if (isFirstCallback) {
          isFirstCallback = false;
          if (authReadyResolve && !authReadyResolved) {
            authLogger.log('Auth ready - resolving promise');
            authReadyResolved = true;
            authReadyResolve();
          }
        }
      }
    });
  }
}

// Web-SDK runtimes (web and electron) initialize immediately — there is no
// native bridge to wait for. Runtimes on the native Firebase plugin defer to
// AppUpdaterGuard / the boot controller. Idempotent either way.
if (!hasCapability('nativeFirebaseAuth') && !isOfflineDemoActiveAtBoot()) {
  initializeAuthListener();
}

// Helper function to wait for auth initialization
// const waitForAuthInitialization = () =>
//   new Promise((resolve) => {
//     const unsubscribe = onAuthStateChanged(AUTH, (user) => {
//       useAuthStore.setState({ authInitialized: true });
//       unsubscribe();
//       resolve(user);
//     });
//   });

export default useAuthStore;

export async function exchangeRefreshTokenForIdToken(refreshToken: string): Promise<string | null> {
  try {
    const response = await axios.post('https://oauth2.googleapis.com/token', null, {
      params: {
        client_id: 'your-client-id',
        client_secret: 'your-client-secret',
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
      },
    });

    const { id_token } = response.data;
    return id_token;
  } catch (error) {
    authLogger.error('Error exchanging refresh token for ID token:', error);
    return null;
  }
}
